import { FC, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CreateIcon from "@mui/icons-material/Create";
import { useNavigate, useLocation } from "react-router-dom";
import { Alert, Snackbar } from "@mui/material";
import { SearchTaskCounter } from "./SearchTaskCounter";
import { TaskCounterTable } from "./TaskCounterTable";
import { useGetTaskCounterTemplates } from "../hooks/useGetTaskCounterTemplates";
import { ButtonA } from "../../common/components/ButtonA";
import { SectionTitle } from "../../common/components/SectionTitle";
import { usePaginationTable } from "../../common/hooks/usePaginationTable";
import { TaskListToggle } from "../../tasks-list-management/components/TaskListToggle";
import { useTaskCounterTemplateSearchValue } from "../hooks/useTaskCounterTemplateSearchValue";
import { useSetTaskCounterTemplateSearch } from "../hooks/useSetTaskCounterTemplateSearch";
import { defaultTaskCounterTemplateSearchState } from "../miscellaneous/taskCounterTemplateSearchAtom";

export const TaskCounterList: FC = () => {
  const {
    control,
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
  } = useForm();

  const searchAtomState = useTaskCounterTemplateSearchValue();
  const setSearchAtomState = useSetTaskCounterTemplateSearch();

  const [searchParams, setSearchParamsLocal] = useState<any>(searchAtomState.criteria);

  const setSearchParams = (newCriteria: any) => {
    setSearchParamsLocal(newCriteria);
    setSearchAtomState((prev) => ({ ...prev, criteria: newCriteria, hasSearched: true }));
  };

  const handleResetAtom = () => {
    setSearchAtomState(defaultTaskCounterTemplateSearchState);
  };

  const {
    templates,
    totalRecords,
    setTemplates,
    setTotalRecords,
    mutationQueryTemplates,
  } = useGetTaskCounterTemplates();

  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    setPage,
    setRowsPerPage,
  } = usePaginationTable(templates);

  const navigate = useNavigate();
  const location = useLocation();
  const [isShowToastSuccess, setIsShowToastSuccess] = useState(false);

  useEffect(() => {
    if (location.state?.importSuccess) {
      setIsShowToastSuccess(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  useEffect(() => {
    setRowsPerPage(-1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const queryParam = {
      pageSize: rowsPerPage,
      pageNum: page + 1,
      companyId: searchParams.companyId ?? "",
      organizationId: searchParams.organizationId ?? "",
      jobTitle: searchParams.jobTitle ?? "",
    };

    mutationQueryTemplates.mutate(queryParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, JSON.stringify(searchParams)]);

  return (
    <div className="mt-9">
      <div className="flex justify-between">
        <SectionTitle name="Task List Management" />
        <div className="flex flex-col items-center gap-2">
          <TaskListToggle activeMode="task-counter" />
          <ButtonA
            name="Create New Template"
            icon={<CreateIcon />}
            customStyle={{
              backgroundColor: "#203C77",
              color: "#fff",
              borderRadius: "50px",
              width: "100%",
              fontWeight: 100,
              "&:hover": { backgroundColor: "#203C77" },
              textTransform: "capitalize",
            }}
            onHandleClick={() => {
              navigate("/create-task-counter-template");
            }}
          />
        </div>
      </div>
      <div>
        <SearchTaskCounter
          control={control}
          register={register}
          handleSubmit={handleSubmit}
          setValue={setValue}
          getValues={getValues}
          setSearchParams={setSearchParams}
          setPage={setPage}
          reset={reset}
          defaultValues={searchAtomState.criteria}
          onAtomReset={handleResetAtom}
        />
        <div className="mt-7">
          <TaskCounterTable
            data={templates}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRecords={totalRecords}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            setData={setTemplates}
            setTotalRecords={setTotalRecords}
            isLoading={mutationQueryTemplates.isLoading}
          />
        </div>
      </div>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={isShowToastSuccess}
        autoHideDuration={8000}
        onClose={(_, reason) => {
          if (reason !== "clickaway") setIsShowToastSuccess(false);
        }}
      >
        <Alert
          onClose={() => setIsShowToastSuccess(false)}
          severity="success"
          variant="filled"
          sx={{ width: "100%" }}
        >
          <p>Import task counter template success</p>
        </Alert>
      </Snackbar>
    </div>
  );
};
